import React, { useState, useEffect, useRef } from 'react';

// 图表进入或接近视口后才执行 Mermaid 的 SVG 生成，避免恢复大量历史记录时
// 同时初始化全部图表。文本 Markdown 不受此组件影响，仍可被浏览器搜索。
const LazyMermaidRenderer = ({
  chart,
  className = '',
  theme = 'dark',
  placeholderHeight = 240,
  ...mermaidProps
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const containerRef = useRef(null);
  const [EnhancedMermaidComponent, setEnhancedMermaidComponent] = useState(null);

  // 使用IntersectionObserver检测元素是否进入视口
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    // IntersectionObserver 在 Chromium 系浏览器中可用；缺失时退化为立即渲染。
    if (!('IntersectionObserver' in window)) {
      setIsVisible(true);
      return undefined;
    }
    
    const observer = new IntersectionObserver(
      (entries) => {
        // 如果元素进入视口，设置为可见
        if (entries[0].isIntersecting) {
          setIsVisible(true);
          // 一旦检测到可见，就不再需要观察
          observer.disconnect();
        }
      },
      {
        rootMargin: '100px 0px', // 提前100px开始加载
        threshold: 0.1 // 当10%的元素可见时触发
      }
    );
    
    observer.observe(container);
    
    return () => {
      observer.disconnect();
    };
  }, []);
  
  // 当元素可见时，动态导入EnhancedMermaid组件
  useEffect(() => {
    if (isVisible && !isLoaded) {
      // 动态导入EnhancedMermaid组件
      import('./enhanced/EnhancedMermaid')
        .then((module) => {
          setEnhancedMermaidComponent(() => module.default);
          setIsLoaded(true);
        })
        .catch(() => {
          // 动态模块加载失败时保持占位，不让单张图表影响整个消息列表。
          setIsLoaded(false);
        });
    }
  }, [isVisible, isLoaded]);
  
  // 简化的占位符样式
  const placeholderStyle = {
    backgroundColor: '#2a2a2a',
    minHeight: `${placeholderHeight}px`,
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#aaa',
    margin: '10px 0',
    padding: '20px',
    position: 'relative',
    overflow: 'hidden',
    border: '1px solid rgba(80, 90, 110, 0.3)'
  };
  
  // 加载动画样式
  const loadingAnimationStyle = {
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '50%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.08), transparent)',
    animation: 'shimmer 1.5s infinite'
  };
  
  // 渲染占位符或实际组件
  return (
    <div ref={containerRef} className={className}>
      {isLoaded && EnhancedMermaidComponent ? (
        <EnhancedMermaidComponent 
          chart={chart}
          theme={theme}
          {...mermaidProps}
        />
      ) : (
        <div style={placeholderStyle}>
          <div>正在准备渲染图表...</div>
          <div style={loadingAnimationStyle}></div>
          <style>{`
            @keyframes shimmer {
              0% { left: -100%; }
              100% { left: 100%; }
            }
          `}</style>
        </div>
      )}
    </div>
  );
};

export default LazyMermaidRenderer; 